import { getUserDetails, getUserSettings, getUser, getEntitlements } from '@surfline/web-common';
import { createSelector } from '@reduxjs/toolkit';
import { useAppSelector } from 'stores/hooks';
import { type Permission, PermissionTypes, type UserPermissions } from 'types/permissions';
import { useTreatments, parseTreatmentWithConfig } from 'utils/treatments';
import { SL_WEB_PREMIUMPLUS_LIMITED_TIME_ACCESS } from 'common/treatments';
import computeLimitedTimePremiumPlusEligibility from 'utils/computeLimitedTimePremiumPlusEligibility';
import { type AppState } from 'stores';
import { getSpotDetails } from './spot';

/**
 * These selectors are meant to abstract redux away from the component as we start to move away from
 * it. This way when we migrate off redux we change internals of the selectors and the data will flow as
 * we expect into the consumers. This allows us to control one source of truth for data coming from
 * redux.
 */

export const getUserWasLoggedOut = (state: AppState) => state.backplane?.user?.wasLoggedOut;
export const getUserPermissions = (state: AppState) => state.backplane?.user?.permissions;
export const getUserCountryCode = (state: AppState) => state.backplane?.user?.countryCode;
export const getUserSubdivisionCode = (state: AppState) => state.backplane?.user?.subdivisionCode;
export const getIsGuestUser = (state: AppState) => state.backplane?.user?.isGuestUser;
export const getCurrentSpotId = (state: AppState): string | null =>
  getSpotDetails(state)?._id || null;

export const getCurrentSubregionId = (state: AppState): string | null =>
  getSpotDetails(state)?.subregion?._id || null;

export const getLtaEntitlement = (state: AppState): string | undefined =>
  state.backplane?.user?.ltaEntitlement;
export const getLtaExpiresAt = (state: AppState): number | undefined =>
  state.backplane?.user?.ltaExpiresAt;
export const getLtaType = (state: AppState): string | undefined => state.backplane?.user?.ltaType;
export const getLtaShowBanner = (state: AppState): boolean =>
  state.backplane?.user?.ltaShowBanner !== undefined ? state.backplane.user.ltaShowBanner : true;
export const getLtaBannerCopy = (state: AppState): string | undefined =>
  state.backplane?.user?.ltaBannerCopy;
export const getHasLtaAccess = (state: AppState): boolean => !!getLtaEntitlement(state);

export const getHasSpotRestrictions = (state: AppState): boolean => {
  const permissions = getUserPermissions(state);
  return permissions?.some((p: Permission) => p?.resourceType === 'spot') ?? false;
};

export const getHasSubregionRestrictions = (state: AppState): boolean => {
  const permissions = getUserPermissions(state);
  return permissions?.some((p: Permission) => p?.resourceType === 'subregion') ?? false;
};

export const getHasBaseSubscription = (state: AppState): boolean => {
  const entitlements = getEntitlements(state) as string[];
  return !!entitlements?.length;
};

export const getIsNonSubscribedWithAccess = (state: AppState): boolean => {
  const ltaType = getLtaType(state);

  if (!ltaType) {
    return false;
  }

  if (ltaType === 'cam-access') {
    return true;
  }

  if (ltaType === 'lta') {
    return !getHasBaseSubscription(state);
  }

  return false;
};

export const getIsLtaGlobal = (state: AppState): boolean => {
  const permissions = getUserPermissions(state);
  if (!permissions?.length) return false;
  const hasSpotRestrictions = permissions.some((p: Permission) => p?.resourceType === 'spot');
  const hasSubregionRestrictions = permissions.some(
    (p: Permission) => p?.resourceType === 'subregion',
  );
  return !hasSpotRestrictions && !hasSubregionRestrictions;
};

const EMPTY_SPOT_IDS: string[] = [];

export const getLtaSpotIds = createSelector([getUserPermissions], (permissions): string[] => {
  if (!permissions?.length) return EMPTY_SPOT_IDS;

  const spotPermission = permissions.find((p: Permission) => p?.resourceType === 'spot');
  if (!spotPermission?.resource) return EMPTY_SPOT_IDS;

  return spotPermission.resource.split(',');
});

const EMPTY_SUBREGION_IDS: string[] = [];

export const getLtaSubregionIds = createSelector([getUserPermissions], (permissions): string[] => {
  if (!permissions?.length) return EMPTY_SUBREGION_IDS;

  const subregionPermission = permissions.find((p: Permission) => p?.resourceType === 'subregion');
  if (!subregionPermission?.resource) return EMPTY_SUBREGION_IDS;

  return subregionPermission.resource.split(',');
});

export const getAllUserPermissions = createSelector(
  [
    getUserPermissions,
    getCurrentSpotId,
    getCurrentSubregionId,
    (_: AppState, isLimitedTimePremiumPlusEligible: boolean) => isLimitedTimePremiumPlusEligible,
  ],
  (
    permissions: Permission[],
    currentSpotId: string | null,
    currentSubregionId: string | null,
    isLimitedTimePremiumPlusEligible: boolean,
  ): UserPermissions => {
    const findPermission = (permissionName: PermissionTypes) => {
      const permission = permissions?.find((item: Permission) => item?.name === permissionName);

      if (!permission) {
        return undefined;
      }

      if (!permission.resourceType || permission.resourceType === '*') {
        return permission;
      }

      if (permission.resourceType === 'spot') {
        const spotIds = permission.resource?.split(',') || [];
        if (!currentSpotId || !spotIds.includes(currentSpotId)) {
          return false;
        }
        return permission;
      }

      if (permission.resourceType === 'subregion') {
        const subregionIds = permission.resource?.split(',') || [];
        if (!currentSubregionId || !subregionIds.includes(currentSubregionId)) {
          return false;
        }
        return permission;
      }

      // Fail closed: any unrecognized scope type (e.g. 'country', legacy
      // 'regionPlan') must not grant access globally.
      return undefined;
    };

    return {
      hasAddMembersPermissions: !!findPermission(PermissionTypes.ADD_MEMBERS),
      hasAdFreePermissions: !!findPermission(PermissionTypes.AD_FREE),
      hasAdFreeCamPermissions: !!findPermission(PermissionTypes.AD_FREE_CAMS),
      hasAdSupportedCamPermissions: !!findPermission(PermissionTypes.AD_SUPPORTED_CAMS),
      hasBuoySpectraPermissions: !!findPermission(PermissionTypes.BUOY_SPECTRA),
      hasCamInsightPermissions:
        isLimitedTimePremiumPlusEligible || !!findPermission(PermissionTypes.CAM_INSIGHTS),
      hasCamRewindPermissions: !!findPermission(PermissionTypes.CAM_REWINDS),
      hasCoreForecastPermissions: !!findPermission(PermissionTypes.CORE_16DAY_FORECAST),
      hasCustomForecastPermissions: !!findPermission(PermissionTypes.CUSTOM_FORECAST),
      hasExtendedTidesPermissions: !!findPermission(PermissionTypes.EXTENDED_TIDES),
      hasExtendedChartLayerPermissions: !!findPermission(PermissionTypes.EXTENDED_CHART_LAYERS),
      hasGraphRewindPermissions:
        isLimitedTimePremiumPlusEligible || !!findPermission(PermissionTypes.GRAPH_REWINDS),
      hasHistoricForecastPermissions: !!findPermission(PermissionTypes.HISTORIC_FORECAST),
      hasMultiCamPermissions: !!findPermission(PermissionTypes.MULTI_CAM),
      hasLineupForecastPermissions: !!findPermission(PermissionTypes.LINEUP),
      hasLiveWindPermissions: !!findPermission(PermissionTypes.LIVE_WIND),
      hasPremiumCamPermissions: !!findPermission(PermissionTypes.HIGH_VALUE_CAMERA_ACCESS),
      hasPremiumAnalysisPermissions: !!findPermission(PermissionTypes.PREMIUM_ANALYSIS),
      hasPremiumArticlesPermissions: !!findPermission(PermissionTypes.PREMIUM_ARTICLES),
      hasSmartClipsPermissions:
        isLimitedTimePremiumPlusEligible || !!findPermission(PermissionTypes.SMART_CLIPS),
      hasSwellSpectraPermissions: !!findPermission(PermissionTypes.SWELL_SPECTRA),
    };
  },
);

// Memoized selector for user permissions with treatments
const getUserPermissionsWithTreatments = createSelector(
  [
    (state: AppState) => state,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (_: AppState, user: any) => user,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (_: AppState, __: any, treatments: any) => treatments,
  ],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: AppState, user: any, treatments: any): UserPermissions => {
    const userLocation = user?.location;
    const userTimeZone = userLocation?.time_zone || 'America/Los_Angeles';
    let isLimitedTimePremiumPlusEligible = false;

    // Check if treatments are defined and not empty
    if (treatments && Object.keys(treatments)?.length > 0) {
      const limitedTimePremiumPlusTreatment = treatments[SL_WEB_PREMIUMPLUS_LIMITED_TIME_ACCESS];
      // Check if the treatment is defined and valid
      if (limitedTimePremiumPlusTreatment) {
        const limitedTimePremiumPlusConfig = parseTreatmentWithConfig(
          limitedTimePremiumPlusTreatment,
        );
        isLimitedTimePremiumPlusEligible = computeLimitedTimePremiumPlusEligibility(
          limitedTimePremiumPlusConfig,
          userTimeZone,
        );
      }
    }

    const userPermissions = getAllUserPermissions(state, isLimitedTimePremiumPlusEligible);

    return userPermissions;
  },
);

export const useUserPermissionStatus = () => {
  const treatments = useTreatments();
  const user = useAppSelector(getUser);
  const permissions = useAppSelector((state: AppState) =>
    getUserPermissionsWithTreatments(state, user, treatments),
  );
  return permissions;
};

export const useUserDetails = () => useAppSelector(getUserDetails);

export const useUserSettings = () => useAppSelector(getUserSettings);

export const useUserCountryCode = () => useAppSelector(getUserCountryCode);

export const useUserUnits = () => {
  const units = useAppSelector(getUserSettings)?.units;
  return units;
};

export const useUserDateFormat = () => {
  const dateFormat = useAppSelector(getUserSettings)?.date?.format;
  return dateFormat;
};

export const useUserPreferredForecastView = () => {
  const preferredForecastView = useAppSelector(getUserSettings)?.preferredForecastView;
  return preferredForecastView;
};

export const useIsInternalUser = (): boolean => {
  const details = useAppSelector(getUserDetails) as Record<string, unknown> | null;
  return !!details?.isInternal;
};

// Re-export the selectors from web-common for backward compatibility
export { getUserDetails, getUserSettings, getEntitlements };
